import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// GLOBAL LOCALE SAFEGUARD (Feb-2026): Some preview/kubernetes containers report
// `navigator.language` as "en-US@posix" (POSIX-augmented) which is NOT a valid
// BCP-47 tag → Intl.DateTimeFormat throws RangeError → crashes any library that
// uses default locales (e.g. TradingView lightweight-charts). Force a canonical
// tag BEFORE any component mounts.
try {
  const raw = String(navigator.language || "en-US");
  if (raw.includes("@") || !/^[a-z]{2,3}(-[A-Za-z0-9]+)*$/.test(raw)) {
    Object.defineProperty(navigator, "language", { get: () => "en-US", configurable: true });
    Object.defineProperty(navigator, "languages", { get: () => ["en-US"], configurable: true });
  }
} catch { /* ignore */ }

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register PWA service worker (production or on HTTPS previews)
// - Registers /service-worker.js
// - Checks for updates every 30 minutes AND on tab focus
// - When a new SW is `installed`, tells it to skipWaiting() → instant reload → app auto-updates
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").then((reg) => {
      // Periodic update checks
      const check = () => reg.update().catch(() => {});
      setInterval(check, 30 * 60 * 1000); // every 30 min
      window.addEventListener("focus", check);
      // Auto-activate a newly installed worker
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            // New content available → activate immediately then reload once
            nw.postMessage({ type: "SKIP_WAITING" });
          }
        });
      });
    }).catch(() => {});
    // Reload the page when the controlling SW changes (avoids infinite reload loops)
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}
